import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BaseViewportPlugin, getTargetType, IScrollInfo, useFramework } from '@/framework';
import { PluginType, type IExportPlugin } from '@/version-types';
import styles from './index.module.less';

const ViewportScrollBar: React.FC = React.memo(() => {
    const framework = useFramework();
    const { edgelessCanvas: viewport, editor, events } = framework.service;
    const [scrollInfo, setScrollInfo] = useState<IScrollInfo | null>(viewport.scrollInfo);
    const [showScrollBar, setShowScrollBar] = useState(false);
    const showScrollBarTimer = useRef<NodeJS.Timeout>();

    const autoHideScrollBar = useCallback(() => {
        clearTimeout(showScrollBarTimer.current);
        showScrollBarTimer.current = setTimeout(() => {
            setShowScrollBar(false);
        }, 1000);
    }, []);

    const forceShowScrollBar = useCallback(() => {
        clearTimeout(showScrollBarTimer.current);
        setShowScrollBar(true);
    }, []);

    useEffect(() => {
        const handle = viewport.onScrollInfoChange((scrollInfo) => setScrollInfo(scrollInfo));
        return () => events.off(handle);
    }, []);

    useEffect(() => {
        const handle = editor.onWheel((event) => {
            const isShowContextMenu = editor.contextMenuVisible;
            if (isShowContextMenu || (editor.disablePick && (event.metaKey || event.ctrlKey))) return;
            if (!viewport.scrollInfo) return;
            setShowScrollBar(true);
            autoHideScrollBar();
        });
        return () => events.off(handle);
    }, []);

    const pressScrollBarType = useRef('');

    const domEventsHandle = {
        onMouseEnter: (_: React.MouseEvent<HTMLDivElement>) => {
            forceShowScrollBar();
        },
        onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            pressScrollBarType.current = getTargetType(e.target);
        },
        onMouseLeave: (_: React.MouseEvent<HTMLDivElement>) => {
            if (pressScrollBarType.current) return;
            autoHideScrollBar();
        },
    };

    useEffect(() => {
        const mouseUpHandle = (_: MouseEvent) => {
            pressScrollBarType.current = '';
            autoHideScrollBar();
        };
        const mouseMoveHandle = (e: MouseEvent) => {
            const pressType = pressScrollBarType.current;
            if (!pressType) return;

            const scrollInfo = viewport.scrollInfo;
            if (!scrollInfo) return;

            const { horizontal, vertical } = scrollInfo;
            const isScrollH = pressType === 'viewport-scrollbar-h';
            const isScrollV = pressType === 'viewport-scrollbar-v';
            const dx = isScrollH ? -e.movementX * horizontal.step : 0;
            const dy = isScrollV ? -e.movementY * vertical.step : 0;

            viewport.relativePan({ x: dx, y: dy });
        };
        document.addEventListener('mouseup', mouseUpHandle);
        document.addEventListener('mousemove', mouseMoveHandle);
        return () => {
            document.removeEventListener('mouseup', mouseUpHandle);
            document.removeEventListener('mousemove', mouseMoveHandle);
        };
    }, []);

    if (!scrollInfo) return null;

    const { width: viewportWidth, height: viewportHeight } = viewport.size;
    const { minThumbSize: MIN_THUMB_SIZE } = scrollInfo;
    const {
        needScrollbar: needHorizontalScrollbar,
        thumbWidth: horizontalThumbWidth,
        totalWidth,
        offset: { left: offsetLeft },
    } = scrollInfo.horizontal;
    const {
        needScrollbar: needVerticalScrollbar,
        thumbHeight: verticalThumbHeight,
        totalHeight,
        offset: { top: offsetTop },
    } = scrollInfo.vertical;

    return (
        <BaseViewportPlugin name="viewport-scroll-bar" zIndex={20}>
            <div
                data-type="viewport-scrollbar-h"
                {...domEventsHandle}
                className={classNames(styles.horizontalBar, styles.scrollBar, {
                    [styles.show]: needHorizontalScrollbar && showScrollBar,
                    [styles.hide]: !(needHorizontalScrollbar && showScrollBar),
                })}
                style={{
                    width: Math.max(MIN_THUMB_SIZE, (horizontalThumbWidth / totalWidth) * viewportWidth),
                    left: Math.min(viewportWidth - MIN_THUMB_SIZE, (offsetLeft / totalWidth) * viewportWidth),
                }}
            ></div>
            <div
                data-type="viewport-scrollbar-v"
                {...domEventsHandle}
                className={classNames(styles.verticalBar, styles.scrollBar, {
                    [styles.show]: needVerticalScrollbar && showScrollBar,
                    [styles.hide]: !(needVerticalScrollbar && showScrollBar),
                })}
                style={{
                    height: Math.max(MIN_THUMB_SIZE, (verticalThumbHeight / totalHeight) * viewportHeight),
                    top: Math.min(viewportHeight - MIN_THUMB_SIZE, (offsetTop / totalHeight) * viewportHeight),
                }}
            ></div>
        </BaseViewportPlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.VIEWPORT,
    sort: 1,
    name: 'viewport-scroll-bar',
    render() {
        return <ViewportScrollBar />;
    },
};

export default exportPlugin;
